import { Injectable, OnDestroy } from '@angular/core';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { CONSTANTS } from '../../../Constants/Constants';
import { TranslateService } from '@ngx-translate/core';
import { IndexDBService } from '../../common/indexDB.service';
import { ITableData } from '../../../models/reports/ITableData';
import { IReportData } from '../../../models/reports/IReportData';
import { Material, MaterialData, MaterialLevelwiseData, PrefabPanel, SQWebArea,SQWebSystem, SQWebController, SubSystem, SystemChildren, SystemComponent, TypicalOfSystemHelper } from '../../../models/dtos';
import { IData } from '../../../models/reports/IData';
import { ReportCheckboxes } from '../../../models/reports/ReportCheckboxes';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { CommonDataService } from '../../common-data.service';
import { ReportTextAlignment } from '../../../models/reports/ReportTextAlignment';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
@Injectable()

export class SolMaterialService implements OnDestroy {
  checkMaterialFlag: boolean = false;
  materialTableData: ITableData[] = [];
  controllerTableData: ITableData[];
  allMaterialData: MaterialLevelwiseData;
  selectedReportCheckboxes: ReportCheckboxes;
  estimateTab: number;
  private typicalOfHelper: Record<string, TypicalOfSystemHelper> = {};

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly translate: TranslateService,
    private readonly indexDBService: IndexDBService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly multilingualPipe: MultilingualCurrencyPipe,

  ) { }

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true): Promise<void> {
    this.selectedReportCheckboxes = this.reportsCommonService.getReportsCheckboxes();
    this.typicalOfHelper = this.commonDataService.getTypicalOfHelpers();
    const tableData = await this.generateTableData(reportId);
    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    const reportTitleKey = reportId === ReportList.ControllerMaterialViewModel ? 'CONTROLLER_MATERIAL' : reportName;
    const reportData: IReportData = {
      selectionName: this.safeTranslate(reportName),
      reportTitle: this.safeTranslate(reportTitleKey),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: tableData,
      pageOrientation: pageOrientation,
      internalReport: internalReport
    }
    this.reportsCommonService.setSelectedReport(reportData);
  }
  private safeTranslate(key?: string): string {
    const k = (key ?? '').trim();
    return k.length ? this.translate.instant(k) : (key ?? '');
  }
  async loadAllMaterialData(): Promise<void> {
    this.allMaterialData = await this.indexDBService.getAllMaterialData();
  }

  async generateMaterialData(): Promise<ITableData[]> {
    const referenceMap = new Map<string, SystemComponent>();
    const projectArea = this.commonDataService.getSQWebProject().areas;
    if (!projectArea || projectArea.length === CONSTANTS.ZERO) {
      return [];
    }
    projectArea.forEach(area =>
      this.traverseArea(area, referenceMap)
    );

    const systemRows: IData[] = [];
    for (const child of referenceMap.values()) {
      const materialItem = child as Material;
      const materialIndicator = this.reportsCommonService.generateMaterialIndicator(materialItem);
      systemRows.push({
        data: [
          { data: child.quantity.toString(), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT },
          { data: child.referenceNumber, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT, materialIndicator: materialIndicator },
          { data: materialItem.modelNumber ?? CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { data: materialItem.mpn ?? CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { data: child.description, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT }
        ]
      });
    }

    if (systemRows.length > CONSTANTS.ZERO) {
      return [{
        tableHeader: [],
        title: [
          { data: this.translate.instant('QTY'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: '30' },
          { data: this.translate.instant('MATERIAL_REF_NUMBER'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: '100' },
          { data: this.translate.instant('MODEL_NUMBER'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: '120', isExpanded: true },
          { data: this.translate.instant('MANUFACTURER_PART_NUMBER'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: '150', isExpanded: true },
          { data: this.translate.instant('DESCRIPTION'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO }
        ],
        data: systemRows,
        tableFooter: [],
        isPageBreakAfter: false,
        isTableBordered: true
      }];
    }

    return [];
  }

  private traverseArea(area: SQWebArea, referenceMap: Map<string, SystemComponent>): void {
    if (!area?.isItemVisible || !area.children) {
      return;
    }
    area.children.forEach(child => {
      if (child.nodeType !== CONSTANTS.NODETYPE_CONTROLLER && !child.isItemVisible) {
        return;
      }
      if (child.nodeType === CONSTANTS.NODETYPE_AREA) {
        this.traverseArea(child as SQWebArea, referenceMap);
      } else if(child.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
        this.handleTypicalOfSystem(child as SQWebSystem, referenceMap);
      } else {
        this.processMaterialForOtherNode((child as SQWebArea).itemUniqueId, referenceMap);
      }
    });
  }

  handleTypicalOfSystem(system: SQWebSystem, referenceMap: Map<string, SystemComponent>): void {
    if (system.hasTypicalOf) {
      const typicalId = system.systemTypicalOfHelperUniqueId;
      const typicalOf = this.typicalOfHelper[typicalId];
      const typicalOfUniqueId = typicalOf?.typicalOfCollectionHolderId ?? system.itemUniqueId;
      this.processMaterialForOtherNode(typicalOfUniqueId, referenceMap);
    } else {
      this.processMaterialForOtherNode(system.itemUniqueId, referenceMap);
    }
  }

  private processMaterialForOtherNode(itemUniqueId: string, referenceMap: Map<string, SystemComponent>): void {
    if (itemUniqueId && this.allMaterialData?.materialsCollection) {
      const materialData: MaterialData = this.allMaterialData.materialsCollection[itemUniqueId];
      if (materialData?.systemChildrenCollection) {
        (materialData.systemChildrenCollection).forEach((sysChild: SystemComponent) => {
          this.processSystemChildForAggregation(sysChild, referenceMap);
        });
      }
    }
  }

  private processSystemChildForAggregation(systemChild: SystemComponent, referenceMap: Map<string, SystemComponent>): void {
    // Handle subsystems by processing their children
    if (systemChild.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
      const subsystem = systemChild as SubSystem;
      if (subsystem.children?.length) {
        for (const child of subsystem.children as SystemComponent[]) {
          this.processSystemChildForAggregation(child, referenceMap);
        }
      }
      return;
    }

    // Skip items with zero quantity or cost
    if (systemChild.quantity <= CONSTANTS.ZERO || systemChild.cost <= CONSTANTS.ZERO) {
      return;
    }

    // Only process master materials
    const material = { ...systemChild } as Material;
    if (!material.isMaster) {
      return;
    }

    // Apply generic name (if applicable) before building key so display and aggregation stay in sync
    this.applyGenericNameIfApplicable(material);

    // Build aggregation key based on expanded checkbox
    const aggregateKey = this.buildDuplicateKey(material);

    // Aggregate or add to map
    const existing = referenceMap.get(aggregateKey);
    if (existing) {
      existing.quantity += material.quantity;
    } else {
      referenceMap.set(aggregateKey, material);
    }
  }

  async generateTableData(reportId: ReportList): Promise<ITableData[]> {
    await this.loadAllMaterialData();
    switch (reportId) {
      case ReportList.MaterialViewModel:
      
        this.materialTableData = await this.generateMaterialData();
        return this.materialTableData;
      
      case ReportList.ControllerMaterialViewModel:  
        this.controllerTableData = this.getMaterialControllerReportData();
        return this.controllerTableData;
      default:
        return [];
    }
  }

  getMaterialControllerReportData(): ITableData[] {
    let projectArea = this.commonDataService.getSQWebProject().areas;
    const tables: ITableData[] = [];
    if (projectArea != null && projectArea.length > CONSTANTS.ZERO) {
      projectArea.forEach(area => {
        this.processAreaRecursively(area, tables);
      });
    }
    return tables;
  }

  // Only one controller can exist within an area, and it is always a direct child of it.
  private getAreaController(area: SQWebArea): SQWebController | undefined {
    return area?.children?.find(child => (child as SQWebArea).nodeType === CONSTANTS.NODETYPE_CONTROLLER) as SQWebController | undefined;
  }

  private processControllerChild(
    controller: SQWebController,
    area: SQWebArea,
    tables: ITableData[]
  ) : void {
    
    const prefabPanelText = this.getPrefabPanelDisplayText(controller.prefabPanelApplied);
    const reserve = controller.productivityReserve ?? CONSTANTS.ZERO;
    const prefabEntry = [{ prefabPanelText, reserve, itemName: area.itemName }];
    tables.push(this.buildPrefabPanelTable(prefabEntry));

    const controllerTableData = this.allMaterialData.materialsCollection[controller.itemUniqueId];
    if (!controllerTableData) return;
    const expanded = this.selectedReportCheckboxes?.expanded?.checked === true;
    const systemRows= this.buildControllerSystemRows(controllerTableData, expanded);

    if(systemRows.length > CONSTANTS.ZERO){
      tables.push(this.buildControllerMaterialTable(systemRows, expanded));
    }
  }

  private buildPrefabPanelTable(mappedDataForPrefabTable: { prefabPanelText: string; reserve: number; itemName: string }[]): ITableData {
    const prefabRows: IData[] = mappedDataForPrefabTable.map(row => ({
            data: [
              { data: row.prefabPanelText === CONSTANTS.EMPTY_STRING ? 'None' : row.prefabPanelText, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
              { data: row.prefabPanelText === CONSTANTS.EMPTY_STRING ? '0.00' : this.multilingualPipe.transform(row.reserve), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT }
            ]
    }));
    return{
      tableHeader: [
        {
          headers: [
            { headerText: 'Area: ' + mappedDataForPrefabTable[CONSTANTS.ZERO].itemName, columnspan: 8 },
          ]
        }
      ],
      title: [
        { data: this.translate.instant('PRE_FAB_PANEL'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: this.translate.instant('RESERVE'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT }
      ],
      data: prefabRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    }
  }

  private buildControllerSystemRows(controllerTableData: MaterialData, expanded: boolean): IData[] {
      const seenKeys = new Set<string>();
      const systemRows: IData[] = [];

      if((controllerTableData.systemChildrenCollection.length === CONSTANTS.ZERO)){
        return [this.buildEmptyControllerRow(expanded)];
      }

       for (const item of controllerTableData.systemChildrenCollection) {
          if (item.quantity <= CONSTANTS.ZERO || item.cost <= CONSTANTS.ZERO){
             continue;
          }
          const mat = item as Material;
          this.applyGenericNameIfApplicable(mat);
          const duplicateKey = expanded
            ? `${item.referenceNumber}_${mat.modelNumber ?? CONSTANTS.EMPTY_STRING}_${mat.manufacturerName ?? CONSTANTS.EMPTY_STRING}`
            : item.referenceNumber
          if (seenKeys.has(duplicateKey)) continue;

          seenKeys.add(duplicateKey)
          systemRows.push(this.buildControllerItemRow(item, mat, expanded));
        }
      return systemRows;
  }

  private buildEmptyControllerRow(expanded: boolean): IData {
    if(expanded){
      return {
        data: [
          { data: 'None', isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { data: CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { data: CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { data: CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { data: CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT }
        ]
      };
    }
    return this.createRowForValueData('None', CONSTANTS.EMPTY_STRING, CONSTANTS.EMPTY_STRING);
  }

  private buildControllerItemRow(item: SystemComponent, mat: Material, expanded: boolean): IData {
    const materialIndicator = this.reportsCommonService.generateMaterialIndicator(mat);
    if(expanded){
      return {
        data: [
          { data: item.referenceNumber, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT, materialIndicator: materialIndicator },
          { data: mat.modelNumber ?? CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT , isExpanded: true},
          { data: mat.mpn ?? CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT, isExpanded: true },
          { data: item.description, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { data: item.quantity.toString(), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT }
        ]
      }
    }
    return {
      data: [
        { data: item.referenceNumber, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT, materialIndicator: materialIndicator },
        { data: item.description, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: item.quantity.toString(), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT }
      ]
    }
  }  

  private buildControllerMaterialTable(systemRows: IData[], expanded: boolean): ITableData {
    const titleExpanded = expanded
              ? [
                { data: this.translate.instant('CONTROLLER_MATERIAL'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT as ReportTextAlignment},
                { data: this.translate.instant('MODEL_NUMBER'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, isExpanded: true, alignment: CONSTANTS.LEFT_ALIGNMENT as ReportTextAlignment },
                { data: this.translate.instant('MANUFACTURER_PART_NUMBER'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, isExpanded: true, alignment: CONSTANTS.LEFT_ALIGNMENT as ReportTextAlignment},
                { data: this.translate.instant('DESCRIPTION'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT as ReportTextAlignment},
                { data: this.translate.instant('QTY'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as ReportTextAlignment }
              ]
            : [
              { data: this.translate.instant('CONTROLLER_MATERIAL'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT as ReportTextAlignment },
              { data: this.translate.instant('DESCRIPTION'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: '350', alignment: CONSTANTS.LEFT_ALIGNMENT as ReportTextAlignment},
              { data: this.translate.instant('QTY'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT as ReportTextAlignment}
            ];

            return {
              tableHeader: [],
              title: titleExpanded,
              data: systemRows,
              tableFooter: [],
              isPageBreakAfter: false,
              isTableBordered: true
            };
  }

  /** Returns null when the typical system was already reported for this area. */
  private getTypicalOfInfo(child: SQWebArea, areaId: string, processedTypicalIds: Set<string>): { materialDataGuid: string; typicalOfDisplay: string; countInArea: number } | null {
    const defaultInfo = { materialDataGuid: child.itemUniqueId, typicalOfDisplay: CONSTANTS.EMPTY_STRING, countInArea: CONSTANTS.ONE };
    if (child.nodeType !== CONSTANTS.NODETYPE_BSETSYSTEM) {
      return defaultInfo;
    }

    const typicalId = (child as unknown as SQWebSystem).systemTypicalOfHelperUniqueId;
    if (!typicalId) {
      return defaultInfo;
    }
    if (processedTypicalIds.has(typicalId)) {
      return null;
    }
    processedTypicalIds.add(typicalId);

    const helper = this.typicalOfHelper[typicalId];
    if (!helper) {
      return defaultInfo;
    }

    const totalCount = helper.typicalOfSystems?.length ?? CONSTANTS.ONE;
    const countInArea = helper.typicalOfSystems?.filter(system => system.isItemVisible && system.parentUniqueId === areaId).length || CONSTANTS.ONE;

    return {
      materialDataGuid: helper.typicalOfCollectionHolderId ?? child.itemUniqueId,
      typicalOfDisplay: `${totalCount} [ ${countInArea} ${this.translate.instant('IN_AREA')} ]`,
      countInArea
    };
  }

  private collectPointsRowsForArea(area: SQWebArea,
    controller: SQWebController,
    combinedPointsRows: IData[],
    combinedTotals: { DO: number; DI: number; AO: number; AI: number; INT: number; FLN: number },
    addedSystemIds: Set<string>
  ): void { 
    if (!area.children || !controller) return;

    const processedTypicalIdsInArea = new Set<string>();

    for (const child of area.children) {
      const childArea = child as SQWebArea;
      if (!childArea.isItemVisible) continue;
      if (childArea.nodeType === CONSTANTS.NODETYPE_AREA || controller.itemUniqueId === childArea.itemUniqueId) continue;                // defer recursion

      const typicalInfo = this.getTypicalOfInfo(childArea, area.itemUniqueId, processedTypicalIdsInArea);
      if (!typicalInfo) continue;                // repeated typical system in this area

      const ioData = this.allMaterialData.materialsCollection[typicalInfo.materialDataGuid];
      if (!ioData) continue;

      const pointsResult = this.getPointsRowsForKey(childArea.itemName, ioData.systemChildrenCollection, typicalInfo.typicalOfDisplay, typicalInfo.countInArea);
      if (!pointsResult?.rows?.length) continue;

      combinedPointsRows.push(...pointsResult.rows);
      addedSystemIds.add(childArea.itemUniqueId);
      combinedTotals.DO += pointsResult.totals.DO;
      combinedTotals.DI += pointsResult.totals.DI;
      combinedTotals.AO += pointsResult.totals.AO;
      combinedTotals.AI += pointsResult.totals.AI;
      combinedTotals.INT += pointsResult.totals.INT;
      combinedTotals.FLN += pointsResult.totals.FLN;
    } 
  }

  private processAreaRecursively(area: SQWebArea, tables: ITableData[]): void {
    if (!area?.isItemVisible) {
      return;
    }

    const controller = this.getAreaController(area);
    if (controller) {
      this.processControllerChild(controller, area, tables);
    }

    // Second: combined Points Table for this area
    const combinedPointsRows: IData[] = [];
    const combinedTotals = { DO: CONSTANTS.ZERO, DI: CONSTANTS.ZERO, AO: CONSTANTS.ZERO, AI: CONSTANTS.ZERO, INT: CONSTANTS.ZERO, FLN: CONSTANTS.ZERO };
    const addedSystemIds = new Set<string>();

    this.collectPointsRowsForArea(area, controller, combinedPointsRows, combinedTotals, addedSystemIds);
    
    //spare capacity for controller data
    if (controller && !addedSystemIds.has(controller.itemUniqueId)) {
      const controllerObj = controller;
      let controllerInt = CONSTANTS.ZERO;
      let controllerFln = CONSTANTS.ZERO;

        const ctrlTotals = {
          DO: controllerObj.spareDOPoints,
          DI: controllerObj.spareDIPoints,
          AO: controllerObj.spareAOVPoints,
          AI: controllerObj.spareAIPoints,
          INT: controllerInt,
          FLN: controllerFln
        };

      const controllerRow: IData = {
        data: [
          { data: 'Spare Capacity', isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
          { data: CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT },
          ...(['DO', 'DI', 'AO', 'AI', 'INT', 'FLN'] as const).map(k => ({
            data: ctrlTotals[k].toString(), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT as ReportTextAlignment
          }))
        ]
      };

        combinedPointsRows.push(controllerRow);
        combinedTotals.DO += ctrlTotals.DO;
        combinedTotals.DI += ctrlTotals.DI;
        combinedTotals.AO += ctrlTotals.AO;
        combinedTotals.AI += ctrlTotals.AI;
        combinedTotals.INT += ctrlTotals.INT;
        combinedTotals.FLN += ctrlTotals.FLN;
    }

    if (combinedPointsRows.length > CONSTANTS.ZERO) {
      tables.push({
        title: [
          { data: this.translate.instant('SYSTEMS'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: '210', alignment: CONSTANTS.LEFT_ALIGNMENT },
          { data: this.translate.instant('TYPICAL_OF'), DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR , isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: '70' },
          ...(['DO', 'DI', 'AO', 'AI', 'INT', 'FLN'] as const).map(k => ({
            data: this.translate.instant(k),DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR , isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, width: '30'
          }
          ))
        ],
        data: combinedPointsRows,
        tableFooter: [
          {
            headers: [
              { headerText: this.translate.instant('CONTROLLER TOTAL POINTS'), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.LEFT_ALIGNMENT },
              { headerText: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, alignment: CONSTANTS.LEFT_ALIGNMENT },
              ...(['DO', 'DI', 'AO', 'AI', 'INT', 'FLN'] as const).map(k => ({
                headerText: combinedTotals[k].toString(), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT
              }))
            ]
          }
        ],
        isPageBreakAfter: false,
        isTableBordered: true
      });
    }

    // Third: after parent tables have been emitted, recurse into child Areas
    if (area.children && area.children.length > CONSTANTS.ZERO) {
      area.children.forEach(child => {
        if ((child as SQWebArea).nodeType === CONSTANTS.NODETYPE_AREA) {
          this.processAreaRecursively(child as SQWebArea, tables);
        }
      });
    }
  }

  getPointsRowsForKey(itemName: string, systemChildrenCollection: SystemChildren, typicalOfDisplay: string = CONSTANTS.EMPTY_STRING, typicalOfCountInArea: number = CONSTANTS.ONE) {
    const totals = {
      DO: CONSTANTS.ZERO,
      DI: CONSTANTS.ZERO,
      AO: CONSTANTS.ZERO,
      AI: CONSTANTS.ZERO,
      INT: CONSTANTS.ZERO,
      FLN: CONSTANTS.ZERO
    };

    if(systemChildrenCollection && systemChildrenCollection.length) {
      systemChildrenCollection.forEach((child: SystemComponent) => {
      totals.DO += child.digitalOutput;
      totals.DI += child.digitalInput;
      totals.AO += child.analogOutput;
      totals.AI += child.analogInput;
      totals.INT += child.integration;
      totals.FLN += child.fln;
    });
    }

    // Typical systems are reported once, so their points are multiplied by the occurrences in the area.
    (['DO', 'DI', 'AO', 'AI', 'INT', 'FLN'] as const).forEach(key => {
      totals[key] *= typicalOfCountInArea;
    });

    const rows: IData[] = [{
      data: [
        { data: itemName, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: typicalOfDisplay, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: totals.DO.toString(), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: totals.DI.toString(), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: totals.AO.toString(), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: totals.AI.toString(), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: totals.INT.toString(), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { data: totals.FLN.toString(), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.RIGHT_ALIGNMENT },
      ]
    }];

    return { rows, totals };
  }


  private getPrefabPanelDisplayText(panelType: PrefabPanel | number): string {
    switch (panelType) {
      case PrefabPanel.PowerWired:
        return this.translate.instant('POWER_WIRED');
      case PrefabPanel.FullyWired:
        return this.translate.instant('FULLY_WIRED');
      case PrefabPanel.FlexWired:
        return this.translate.instant('FLEX_WIRED');
      case CONSTANTS.ZERO: // legacy value used for "no prefab panel"
        return 'NONE';
      default:
        return CONSTANTS.EMPTY_STRING;
    }
  }

  private createRowForValueData(referenceNumber: string, description: string, qty: string): IData {
    const rowData: IData = {
      data: [
        { data: referenceNumber === CONSTANTS.EMPTY_STRING ? 'none' : referenceNumber, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: description, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: qty, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.CENTER_ALIGNMENT },
      ]
    };
    return rowData;
  }

  private applyGenericNameIfApplicable(material: Material): void {
    if (this.reportsCommonService.isGenericNameApplicable()) {
      material.description = this.reportsCommonService.getGenericNameBySSN(material.ssn) || material.description;
    }
  }

  private buildDuplicateKey(child: SystemComponent): string {
    const materialItem = child as unknown as Material;
    const description = child.description ?? CONSTANTS.EMPTY_STRING;
    const expanded = this.selectedReportCheckboxes?.expanded?.checked === true;

    return expanded
      ? `${child.referenceNumber}_${materialItem.modelNumber ?? CONSTANTS.EMPTY_STRING}_${materialItem.manufacturerName ?? CONSTANTS.EMPTY_STRING}_${description}`
      : `${child.referenceNumber}_${description}`;
  }

  ngOnDestroy(): void {
    this.allMaterialData = null;
    this.materialTableData = [];
    this.controllerTableData = [];
  }

}